import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Check, 
  Sparkles,
  ArrowRight,
  TrendingUp,
  Users,
  DollarSign
} from 'lucide-react';
import Logo from '@/components/Logo';
import { Button } from "@/components/ui/button";

const MenuExtractionLimitReached: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get items count from location state, sessionStorage, or default
  const itemsExtracted = location.state?.itemsExtracted || 
    sessionStorage.getItem('cached_menu_items') || 
    46;

  return (
    <div className="min-h-screen bg-gradient-to-b from-dineai-blue to-dineai-blue/95 relative overflow-hidden">
      {/* Elegant animated background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-dineai-yellow/10 blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 rounded-full bg-white/5 blur-3xl animate-pulse delay-700"></div>
      </div>

      <div className="container mx-auto px-4 flex items-center justify-center min-h-screen relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-2xl"
        >
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <Logo className="text-white h-10" />
          </div>

          {/* Main Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-10 md:p-12 text-center">
            
            {/* Success Badge */}
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-green-500/20 px-4 py-2 rounded-full mb-6"
            >
              <Sparkles className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-semibold">Extraction Successful</span>
            </motion.div>

            {/* Success Message */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Great news! Your menu is already loaded and ready!
              </h1>
              <p className="text-2xl text-dineai-yellow font-bold mb-6">
                {itemsExtracted} items ready
              </p>
            </motion.div>

            {/* Value Proposition */}
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-lg text-white/90 mb-8 leading-relaxed"
            >
              See why many restaurant owners trust Dine AI to delight their customers and grow their business
            </motion.p>

            {/* Key Benefits */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white/5 rounded-2xl p-6 mb-8"
            >
              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-dineai-yellow/20 rounded-full flex items-center justify-center mb-3">
                    <TrendingUp className="w-6 h-6 text-dineai-yellow" />
                  </div>
                  <p className="text-white font-semibold">15-20% Revenue Boost</p>
                  <p className="text-white/60 text-sm mt-1">Proven results</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-dineai-yellow/20 rounded-full flex items-center justify-center mb-3">
                    <Users className="w-6 h-6 text-dineai-yellow" />
                  </div>
                  <p className="text-white font-semibold">Happy Customers</p>
                  <p className="text-white/60 text-sm mt-1">Growing community</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-dineai-yellow/20 rounded-full flex items-center justify-center mb-3">
                    <DollarSign className="w-6 h-6 text-dineai-yellow" />
                  </div>
                  <p className="text-white font-semibold">30-Day Free Trial</p>
                  <p className="text-white/60 text-sm mt-1">Cancel anytime</p>
                </div>
              </div>
            </motion.div>

            {/* Trust Signals */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex items-center justify-center gap-6 text-white/70 text-sm mb-8"
            >
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                30-day free trial
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                Cancel anytime
              </span>
            </motion.div>

            {/* Main CTA */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Button 
                onClick={() => {
                  // For cached menus, we need to handle differently
                  // Since we don't have a real extraction to claim
                  const isCached = sessionStorage.getItem('cached_extraction') === 'true';
                  
                  if (isCached) {
                    // Clear cached data and go to signup
                    sessionStorage.removeItem('cached_extraction');
                    sessionStorage.removeItem('cached_menu_items');
                    
                    navigate('/restaurant-signup', {
                      state: {
                        fromConversion: true,
                        message: 'Sign up to start uploading your menu'
                      }
                    });
                  } else {
                    // Regular extraction - needs to be claimed after signup
                    navigate('/restaurant-signup', {
                      state: {
                        fromRateLimit: true,
                        extractionComplete: true,
                        itemsExtracted: itemsExtracted,
                        restaurantName: 'Your Restaurant'
                      }
                    });
                  }
                }}
                className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-4 px-10 text-lg font-bold rounded-full shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 mx-auto"
                size="lg"
              >
                Join Successful Restaurants
                <ArrowRight className="w-5 h-5" />
              </Button>
            </motion.div>

            {/* Social Proof */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-8 pt-8 border-t border-white/10"
            >
              <p className="text-white/60 text-sm">
                "Dine AI increased our average order value by 18% in the first month" 
                <span className="block mt-2 text-white/80">- Sarah M., Restaurant Owner</span>
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MenuExtractionLimitReached;